import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Package, Clock, ExternalLink } from 'lucide-react';
import { formatSize } from '../utils/variantUtils';
import TrackingTimeline from './TrackingTimeline';

const TrackingModal = ({ isOpen, onClose, order }) => {
    const { t } = useTranslation();

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !order) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
            <div className="bg-white rounded-[2.5rem] w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="px-8 py-6 border-b border-gray-50 flex justify-between items-center bg-white sticky top-0 z-20">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
                            <Clock size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-gray-900 tracking-tight">{t('delivery_tracking.timeline.title')}</h2>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">#{order.orderId}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
                    >
                        <X size={24} className="text-gray-300 group-hover:text-gray-600" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8">
                    {/* Order Summary Card */}
                    <div className="bg-gray-50/50 rounded-3xl p-6 border border-gray-100 mb-8">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-gray-400 shrink-0 shadow-sm">
                                <Package size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">{t('confirmation_modal.sections.product')}</p>
                                <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2">{order.productName}</h3>
                                {order.variant && (
                                    <p className="text-xs text-indigo-500 font-bold mt-1 uppercase tracking-tight">• <bdi>{typeof order.variant === 'object' ? [order.variant.color, formatSize(order.variant.size)].filter(Boolean).join(' - ') : formatSize(order.variant)}</bdi></p>
                                )}
                            </div>
                            {order.shipping?.trackingNumber && (
                                <div className="text-right">
                                    <p className="text-[9px] font-black text-green-400 uppercase tracking-widest mb-1">Tracking</p>
                                    <p className="text-xs font-black text-green-600 font-mono tracking-tighter">{order.shipping.trackingNumber}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="pl-2">
                        <TrackingTimeline
                            history={order.delivery?.statusHistory}
                            currentStatus={order.delivery?.status}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-6 border-t border-gray-50 bg-gray-50/30 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-8 py-3 bg-gray-900 hover:bg-black text-white rounded-2xl font-black transition-all shadow-lg shadow-gray-200 uppercase tracking-widest text-[11px]"
                    >
                        {t('common.close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrackingModal;
